import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { useCreateCasinoSessionMutation } from '@/store/api/casinoApi';
import { getPlatform } from '@/utils';
import { useIsAuthenticated } from './redux';
import { ApiError } from '@/types/error';

type CasinoLaunchGame = {
  id: number;
};

export function useCasinoGameplay() {
  const isAuthenticated = useIsAuthenticated();
  const [activeGameId, setActiveGameId] = useState<number | null>(null);
  const [sessionUrl, setSessionUrl] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [createSession, { isLoading: sessionLoading }] = useCreateCasinoSessionMutation();

  const handleGameClick = useCallback(
    async (game: CasinoLaunchGame) => {
      if (sessionLoading) return;

      if (!isAuthenticated) {
        toast.error('Please login to access!', { id: 'game-session' });
        return;
      }

      setActiveGameId(game.id);
      const toastId = toast.loading('Launching game...');

      try {
        const platform = getPlatform();
        const result = await createSession({ id: game.id, platform }).unwrap();

        if (!result?.url) {
          toast.error(result?.message || 'Failed to create game session', { id: toastId });
          return;
        }

        toast.dismiss(toastId);
        if (platform === 'Desktop') {
          window.location.href = result.url;
          return;
        }

        setSessionUrl(result.url);
        setIsModalOpen(true);
      } catch (error) {
        toast.error((error as ApiError)?.data?.message || 'Failed to create game session', {
          id: toastId,
        });
      } finally {
        setActiveGameId(null);
      }
    },
    [createSession, isAuthenticated, sessionLoading]
  );

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setSessionUrl('');
  }, []);

  return {
    activeGameId,
    sessionUrl,
    isModalOpen,
    sessionLoading,
    handleGameClick,
    closeModal,
  };
}

export default useCasinoGameplay;
